import React from 'react';
import styled from 'styled-components/macro';
import { MdContentCopy } from 'react-icons/md';
import { theme } from 'lib/style';
import { useCustomReportsContext, removeRecurring } from 'lib/context';
import { ModalDelete } from 'lib/components';
import EditIcon from 'lib/images/EditIcon';
import DeleteIcon from 'lib/images/DeleteIcon';
import { RecurringModal } from './RecurringModal';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 24px 20px 24px 24px;
  border-radius: 4px;
  box-shadow: 0 4px 12px 0 #1d1e240a;
  border: solid 1px #f7f8fa;
  margin-top: 16px;
`;

const Header = styled.div`
  font-size: 18px;
  font-weight: bold;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.33;
  letter-spacing: normal;
  color: #272a32;
`;

const EmailContainer = styled.div`
  display: block;
  margin-top: 8px;
`;

const Email = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  letter-spacing: normal;
  color: #4e5461;
  margin-right: 12px;
`;

const ActionIcons = styled.div`
  > svg {
    cursor: pointer;
    opacity: 0.9;
    margin-right: 12px;
    &:hover {
      opacity: 1;
    }
  }
`;

type Props = {
  reportId: string;
  recurring: any;
  setCustomReport: (reportId: string) => void;
};

const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const getRecurringTitle = (recurring: any) => {
  const frequency = recurring.frequency.split(';');
  const deliveryTime = recurring.deliveryTime.split(';');

  let frequencyDetails = capitalize(frequency[1]);
  if (frequency[0] === 'weekly') {
    frequencyDetails = frequency[1]
      .split(',')
      .map((day: string) => capitalize(day))
      .join(', ');
  }
  if (frequency[0] === 'monthly') {
    frequencyDetails = `On ${frequency[1]} day of month`;
  }

  return `${capitalize(frequency[0])} • ${frequencyDetails} • ${
    deliveryTime[0]
  } ${deliveryTime[1]} ${deliveryTime[2]}`;
};

export const RecurringItem = (props: Props) => {
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [createNewRecurring, setCreateNewRecurring] = React.useState(false);

  const { recurring, reportId, setCustomReport } = props;

  const { dispatch } = useCustomReportsContext();

  return (
    <Container key={recurring.recurringId}>
      <div>
        <Header>{getRecurringTitle(recurring)}</Header>
        <EmailContainer>
          {recurring.recipients.map((recipient: string) => (
            <Email key={recipient}>{recipient};</Email>
          ))}
        </EmailContainer>
      </div>
      <ActionIcons>
        <MdContentCopy
          onClick={() => {
            setCreateNewRecurring(true);
            setShowEditModal(true);
          }}
          size='24px'
          color={theme.palette.black_1_100}
        />
        <EditIcon
          onClick={() => {
            setCreateNewRecurring(false);
            setShowEditModal(true);
          }}
          color={theme.palette.black_1_100}
        />
        <DeleteIcon
          onClick={() => setShowDeleteModal(true)}
          color={theme.palette.buttonDelete}
        />
      </ActionIcons>

      {showDeleteModal && (
        <ModalDelete
          whiteButtonText='Yes, Delete'
          orangeButtonText='No, Cancel'
          title='Delete recurring?'
          text={`This action can’t be undone.`}
          handleModalClose={() => setShowDeleteModal(false)}
          onClickWhiteButton={async () => {
            setShowDeleteModal(false);
            await removeRecurring({
              dispatch,
              data: {
                reportId,
                recurringId: recurring.recurringId,
              },
            });
          }}
          onClickOrangeButton={() => setShowDeleteModal(false)}
        />
      )}
      {showEditModal && (
        <RecurringModal
          reportId={reportId}
          recurring={recurring}
          handleModalClose={() => setShowEditModal(false)}
          createNew={createNewRecurring}
          setCustomReport={setCustomReport}
        />
      )}
    </Container>
  );
};
